// hooks/useUploadImage.js

import { useState, useCallback } from "react";
import axios from "axios";
import useLocalStorage from "hooks/useLocalStorage";

const axiosInstance = axios.create({
  baseURL: window?.globalConfig.rvlApiUrl
});

/**
 * Custom hook to handle image uploads to the API
 * @returns {object} An object containing the upload function and the response state
 */
const useUploadImage = () => {
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionToken] = useLocalStorage("sessionToken", "");
  const [dealerSessionToken] = useLocalStorage("sessionToken_dealer", "");
  const tokenToUse = sessionToken || dealerSessionToken;
  /**
   * Uploads an image to the API
   * @param {File} file - The image file to upload
   * @param {string} vehicleID - The vehicle ID for the API request
   * @returns {void}
   */
  const uploadImage = useCallback(async (file, vehicleID) => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const reader = new FileReader();

      reader.onloadend = async () => {
        const base64String = reader.result.split(",")[1]; // Extract base64 string
        const payload = {
          sessionToken: tokenToUse,
          photoPath: file.name,
          photoBase64: base64String,
          resizeImages: true,
          vehicleID,
        };

        try {
          const res = await axiosInstance.post("/api/Inspection/AddPictures", payload);
          const {success, message} = res.data;
          if (success) {
            setResponse(res.data);
          }
          else {
            setError(new Error(message))
          }
        } catch (err) {
          setError(err);
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError(new Error("Failed to read file"));
        setLoading(false);
      };

      reader.readAsDataURL(file); // Use readAsDataURL to get base64 string
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  }, [sessionToken]);

  return { uploadImage, response, error, loading };
};

export default useUploadImage;
